import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { CheckCheck, Download, Pause, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDuration } from "@/lib/chat/formatarDuracao";
import { TextoComLinks } from "@/components/links/TextoComLinks";

/**
 * <AudioMessageBubble> — player de uma mensagem de áudio (story F2, feature
 * "notas de voz"). Componente PURO/apresentador: recebe uma URL já tocável
 * (o caller resolve a signed-URL do storage) e renderiza waveform + controles.
 * NÃO assina/baixa do storage, NÃO conhece chat/atendimentos, NÃO faz upload.
 * Irmão da F1 (gravador) — independente dela.
 */
export interface AudioMessageBubbleProps {
  /** URL de áudio JÁ tocável (o caller resolve a signed-URL do storage). */
  src: string;
  /** duração conhecida (dica); cai pro metadata do <audio> se ausente. */
  durationSec?: number;
  mime?: string;
  caption?: string;
  /** alinhamento/estilo: mensagem própria vs recebida. */
  isOwn?: boolean;
  /** já ouvido (mostra indicador). */
  played?: boolean;
  /** dispara 1x no 1º play — o caller decide o read-receipt. */
  onPlayed?: () => void;
  /** default true; parceiro recebe false. */
  downloadable?: boolean;
  className?: string;
}

const WAVEFORM_BARS = 48;
const SPEEDS = [1, 1.5, 2] as const;

/** Extensão de arquivo a partir do mime, p/ o nome do download. */
function extFromMime(mime?: string): string {
  if (!mime) return "";
  if (mime.includes("webm")) return ".webm";
  if (mime.includes("mp4") || mime.includes("m4a") || mime.includes("aac")) return ".m4a";
  if (mime.includes("mpeg") || mime.includes("mp3")) return ".mp3";
  if (mime.includes("ogg")) return ".ogg";
  if (mime.includes("wav")) return ".wav";
  return "";
}

/**
 * Extrai `bars` picos normalizados (0..1) do canal 0 do AudioBuffer.
 * Puro → testável sem browser.
 */
export function computePeaks(audioBuffer: AudioBuffer, bars: number): number[] {
  const data = audioBuffer.getChannelData(0);
  const block = Math.floor(data.length / bars) || 1;
  const peaks: number[] = [];
  let max = 0;
  for (let i = 0; i < bars; i++) {
    let peak = 0;
    const start = i * block;
    for (let j = 0; j < block; j++) {
      const v = Math.abs(data[start + j] || 0);
      if (v > peak) peak = v;
    }
    peaks.push(peak);
    if (peak > max) max = peak;
  }
  // Normaliza pelo pico global (evita waveform "chapado" em áudio baixo).
  return max > 0 ? peaks.map((p) => p / max) : peaks;
}

export function AudioMessageBubble({
  src,
  durationSec,
  mime,
  caption,
  isOwn = false,
  played = false,
  onPlayed,
  downloadable = true,
  className,
}: AudioMessageBubbleProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const playedFiredRef = useRef(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [peaks, setPeaks] = useState<number[] | null>(null);
  const [decodeFailed, setDecodeFailed] = useState(false);
  const [playbackError, setPlaybackError] = useState(false);
  const [totalSec, setTotalSec] = useState<number>(durationSec ?? 0);
  const [currentSec, setCurrentSec] = useState(0);
  const [speedIdx, setSpeedIdx] = useState(0);

  // Identidade ESTÁVEL do áudio: o caminho da storage-key, SEM a `?expires&sig`.
  // O /atendimentos e o portal fazem POLL (5s) e o backend re-assina a signed-url
  // FRESCA a cada request → o `src` muda de assinatura toda hora, MESMO sendo o
  // MESMO áudio. Se o decode/reset e o `<audio>` reagissem ao `src` cru, a cada poll
  // o card oscilaria (peaks→null→canvas) e o play cortaria (recarrega o `<audio>`).
  // `srcId` (caminho) só muda quando é OUTRO áudio → é a dependência real.
  const srcId = useMemo(() => src.split("?")[0], [src]);

  // URL tocável estável: só adota a signed-url nova quando o ÁUDIO (srcId) muda —
  // assinatura-só-diferente do mesmo áudio NÃO recarrega o `<audio>` (não corta o play).
  const [playableSrc, setPlayableSrc] = useState(src);
  useEffect(() => {
    setPlayableSrc(src);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só quando o srcId muda; a
    // troca de assinatura (mesmo srcId) é ignorada de propósito (evita reload/corte).
  }, [srcId]);

  // Decode do waveform (best-effort). Falha (ex.: Safari+webm) → barra simples.
  // Também é o ponto de RESET ao trocar de mensagem: keya em `srcId` (não `src`) →
  // re-assinatura do poll NÃO reseta peaks/velocidade/ouvido/posição. O componente
  // é reusado (lista keyed por índice) — sem zerar ao trocar de áudio, o estado da
  // mensagem anterior vazaria. O <audio> zera playbackRate/currentTime sozinho no swap.
  useEffect(() => {
    let cancelled = false;
    playedFiredRef.current = false;
    setIsPlaying(false);
    setPlaybackError(false);
    setCurrentSec(0);
    setSpeedIdx(0);
    setTotalSec(durationSec ?? 0);
    const audioEl = audioRef.current;
    if (audioEl) audioEl.playbackRate = 1;
    setPeaks(null);
    setDecodeFailed(false);

    const AudioCtx =
      typeof window !== "undefined"
        ? window.AudioContext ||
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
        : undefined;
    if (!AudioCtx || typeof fetch === "undefined") {
      setDecodeFailed(true);
      return;
    }

    let ctx: AudioContext;
    try {
      ctx = new AudioCtx();
    } catch {
      setDecodeFailed(true);
      return;
    }

    fetch(src)
      .then((r) => r.arrayBuffer())
      .then((buf) => ctx.decodeAudioData(buf))
      .then((audioBuf) => {
        if (cancelled) return;
        setPeaks(computePeaks(audioBuf, WAVEFORM_BARS));
        if (durationSec == null && Number.isFinite(audioBuf.duration)) {
          setTotalSec(audioBuf.duration);
        }
      })
      .catch(() => {
        if (!cancelled) setDecodeFailed(true);
      })
      .finally(() => {
        void ctx.close().catch(() => undefined);
      });

    return () => {
      cancelled = true;
    };
    // Keya em `srcId` (não `src`): a re-assinatura do poll (mesmo áudio) não reseta.
    // `fetch(src)` usa a signed-url FRESCA no instante da troca de áudio (correta).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srcId, durationSec]);

  // Desenha o waveform + overlay de progresso.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !peaks) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    const frac = totalSec > 0 ? currentSec / totalSec : 0;
    // Cores do waveform vindas dos TOKENS de tema (adapta a light/dark/tema-custom),
    // cientes do tom da bolha: own (escura) = claro; other (clara) = escuro/muted.
    // Substitui os `rgb()` hardcoded (azul/slate) anteriores.
    const cs = getComputedStyle(canvas);
    const cssVar = (name: string) => cs.getPropertyValue(name).trim();
    const playedColor = isOwn ? `hsl(0 0% 100%)` : `hsl(${cssVar("--primary")})`;
    const restColor = isOwn
      ? `hsl(0 0% 100% / 0.4)`
      : `hsl(${cssVar("--muted-foreground")} / 0.55)`;
    ctx.clearRect(0, 0, w, h);
    const barW = w / peaks.length;
    for (let i = 0; i < peaks.length; i++) {
      const barH = Math.max(2, peaks[i] * h);
      const x = i * barW;
      const y = (h - barH) / 2;
      ctx.fillStyle = i / peaks.length <= frac ? playedColor : restColor;
      ctx.fillRect(x, y, Math.max(1, barW - 1), barH);
    }
  }, [peaks, currentSec, totalSec, isOwn]);

  // Cleanup: pausa o áudio no unmount.
  useEffect(() => {
    const el = audioRef.current;
    return () => {
      el?.pause();
    };
  }, []);

  const fireOnPlayedOnce = useCallback(() => {
    if (!playedFiredRef.current) {
      playedFiredRef.current = true;
      onPlayed?.();
    }
  }, [onPlayed]);

  const togglePlay = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play()
        .then(() => {
          setIsPlaying(true);
          fireOnPlayedOnce();
        })
        .catch(() => {
          setPlaybackError(true);
          setIsPlaying(false);
        });
    } else {
      el.pause();
      setIsPlaying(false);
    }
  }, [fireOnPlayedOnce]);

  const cycleSpeed = useCallback(() => {
    setSpeedIdx((prev) => {
      const next = (prev + 1) % SPEEDS.length;
      const el = audioRef.current;
      if (el) el.playbackRate = SPEEDS[next];
      return next;
    });
  }, []);

  const seekFromPointer = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      // arrasto: só quando o botão está pressionado; clique inicial sempre passa.
      if (e.type === "pointermove" && e.buttons !== 1) return;
      const el = audioRef.current;
      if (!el || totalSec <= 0) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / (rect.width || 1)));
      const t = frac * totalSec;
      el.currentTime = t;
      setCurrentSec(t);
    },
    [totalSec]
  );

  const handleDownload = useCallback(() => {
    const a = document.createElement("a");
    a.href = src;
    a.download = `nota-de-voz${extFromMime(mime)}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [src, mime]);

  const speed = SPEEDS[speedIdx];
  const progressPct = totalSec > 0 ? (currentSec / totalSec) * 100 : 0;

  return (
    <div
      className={cn(
        "flex max-w-sm items-center gap-2 rounded-lg border p-2",
        isOwn ? "ml-auto border-white/20" : "border-border",
        className
      )}
    >
      <audio
        ref={audioRef}
        src={playableSrc}
        preload="metadata"
        className="hidden"
        onTimeUpdate={(e) => setCurrentSec(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => {
          if (durationSec == null && Number.isFinite(e.currentTarget.duration)) {
            setTotalSec(e.currentTarget.duration);
          }
        }}
        onEnded={() => {
          setIsPlaying(false);
          fireOnPlayedOnce();
        }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onError={() => {
          // Auto-heal: a signed-url pode ter EXPIRADO (TTL 7d) enquanto o card ficou
          // com a url congelada (`playableSrc`) — ou uma falha de rede transitória.
          // Se há uma `src` mais fresca (o poll re-assinou), adota-a e deixa recarregar;
          // só marca erro real se a própria url fresca também falhar.
          if (playableSrc !== src) setPlayableSrc(src);
          else setPlaybackError(true);
        }}
      />

      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={isPlaying ? "Pausar" : "Tocar"}
        onClick={togglePlay}
        disabled={playbackError}
      >
        {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
      </Button>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {playbackError ? (
          // altura h-8 (igual ao canvas/barra) → cair no estado de erro não encolhe o card.
          <span
            className={cn(
              "flex h-8 items-center text-xs",
              isOwn ? "text-white/80" : "text-muted-foreground"
            )}
          >
            Não foi possível reproduzir neste navegador.
          </span>
        ) : peaks && !decodeFailed ? (
          <div
            role="slider"
            aria-label="Progresso do áudio"
            aria-valuemin={0}
            aria-valuemax={Math.round(totalSec)}
            aria-valuenow={Math.round(currentSec)}
            className="cursor-pointer"
            onPointerDown={seekFromPointer}
            onPointerMove={seekFromPointer}
          >
            <canvas ref={canvasRef} width={192} height={32} className="h-8 w-full" />
          </div>
        ) : (
          <div
            role="slider"
            aria-label="Progresso do áudio"
            aria-valuemin={0}
            aria-valuemax={Math.round(totalSec)}
            aria-valuenow={Math.round(currentSec)}
            // altura FIXA h-8 (igual ao canvas) → o swap decode-pendente↔decodado
            // não muda a altura do card (evita o "aumenta/diminui").
            className="flex h-8 w-full cursor-pointer items-center"
            onPointerDown={seekFromPointer}
            onPointerMove={seekFromPointer}
          >
            <div
              className={cn(
                "h-2 w-full rounded-full",
                isOwn ? "bg-white/25" : "bg-muted-foreground/25"
              )}
            >
              <div
                className={cn("h-2 rounded-full", isOwn ? "bg-white" : "bg-primary")}
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        )}

        <div
          className={cn(
            "flex items-center gap-2 text-xs",
            isOwn ? "text-white/80" : "text-muted-foreground"
          )}
        >
          <span className="tabular-nums">
            {formatDuration(currentSec)} / {formatDuration(totalSec)}
          </span>
          {played && (
            <CheckCheck
              className={cn("h-3.5 w-3.5", isOwn ? "text-white" : "text-primary")}
              aria-label="Ouvido"
            />
          )}
        </div>

        {/* Legenda de anexo (prévia de link): o link fica clicável, mas nunca ganha cartão. */}
        {caption && (
          <p className="break-words text-sm">
            <TextoComLinks texto={caption} tom={isOwn ? "propria" : "neutro"} />
          </p>
        )}
      </div>

      {!playbackError && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label="Velocidade de reprodução"
          className="tabular-nums"
          onClick={cycleSpeed}
        >
          {speed}×
        </Button>
      )}

      {downloadable && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Baixar áudio"
          onClick={handleDownload}
        >
          <Download className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
